import { useEffect, useState, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Check, X, ArrowRight, Phone, Users, MessageSquare, Clock,
  Sparkles, Building2, Calendar, CheckCircle2,
} from "lucide-react";
import { API, AuthContext } from "../App";
import { useIsMobileApp } from "../hooks/useIsMobileApp";
import { getActiveDiscount, applyDiscount } from "../utils/planDiscounts";

import "../styles/marketing.css";
import Seo from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Reveal from "../components/marketing/Reveal";
import BookDemoModal from "./BookDemoModal";

const ALL_FEATURES = [
  { key: "max_users", label: "Team Members", type: "limit" },
  { key: "unlimited_technicians", label: "Unlimited Technicians", type: "boolean" },
  { key: "sms_limit", label: "SMS Messages/Month", type: "limit" },
  { key: "minutes_limit", label: "Voice Minutes/Month", type: "limit" },
  { key: "dedicated_lines", label: "Dedicated Phone Lines", type: "limit" },
  { key: "call_masking", label: "Call Masking", type: "boolean" },
  { key: "joby_automation", label: "Smart Automations (Fieldz AI)", type: "boolean" },
  { key: "quickbooks_integration", label: "QuickBooks Integration", type: "boolean" },
  { key: "customer_portal", label: "Customer Portal", type: "boolean" },
  { key: "chat_support_24_7", label: "24/7 Priority Support", type: "boolean" },
  { key: "max_customers", label: "Customer Records", type: "limit", unlimited: true },
  { key: "max_jobs_per_month", label: "Jobs per Month", type: "limit", unlimited: true },
];

const PRICING_FAQS = [
  { q: "Can I change my plan later?", a: "Yes! You can upgrade or downgrade your plan at any time from your dashboard settings. Changes take effect immediately." },
  { q: "Is there a free trial?", a: "Yes, all plans come with a 7-day free trial. No credit card required to start. During the trial, phone number purchases, SMS, and voice calls are restricted." },
  { q: "What happens if I exceed my limits?", a: "We'll notify you when you're approaching your limits. You can upgrade your plan or purchase additional capacity as needed." },
  { q: "Can I cancel anytime?", a: "Absolutely. You can cancel your subscription at any time. You'll retain access until the end of your billing period." },
  { q: "Do you offer custom enterprise plans?", a: "Yes! Book a demo or contact our team for custom pricing and features tailored to your business needs." },
];

const getFeatureValue = (plan, feature) => {
  const value = plan[feature.key];
  if (feature.type === "boolean") return value === true;
  if (feature.type === "limit") {
    if (feature.unlimited && (value === -1 || value === null || value === undefined)) return "Unlimited";
    if (typeof value === "number") return value.toLocaleString();
    return value || "-";
  }
  return value;
};

const Stat = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1.5 text-sm">
    <Icon className="w-3.5 h-3.5 text-slate-400" />
    <span className="text-slate-600">{label}</span>
  </div>
);

const PricingPage = () => {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const { hash } = useLocation();
  const isMobileApp = useIsMobileApp();
  const [plans, setPlans] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoPlan, setDemoPlan] = useState(null);
  const openDemoFor = (plan = null) => { setDemoPlan(plan); setDemoOpen(true); };

  useEffect(() => {
    if (isMobileApp) navigate("/", { replace: true });
  }, [isMobileApp, navigate]);

  useEffect(() => {
    axios
      .get(`${API}/plans`)
      .then((res) => {
        setPlans((res.data.plans || []).filter((p) => p.is_active).sort((a, b) => a.price - b.price));
        setDiscounts(res.data.discounts || []);
      })
      .catch(() => {})
      .finally(() => setLoadingPlans(false));
  }, []);

  useEffect(() => {
    if (!hash || loadingPlans) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [hash, loadingPlans]);

  const handleSelectPlan = (plan) => {
    const hasNoCheckoutTarget =
      (typeof plan?.price !== "number" || plan.price <= 0) ||
      (!plan?.stripe_price_id && !plan?.stripe_price_id_annual);
    if (plan?.is_contact_us || hasNoCheckoutTarget) {
      openDemoFor(plan);
      return;
    }
    if (user) {
      navigate("/dashboard/settings?tab=my-plan");
    } else {
      navigate("/register", { state: { selectedPlan: plan.plan_id } });
    }
  };

  const singlePlans = plans.filter((p) => (p.plan_tier || "single") === "single");
  const mlPlans = plans.filter((p) => p.plan_tier === "multi_location");

  const renderPrice = (plan) => {
    // Cycle-aware: monthly discounts apply to the monthly price; annual
    // discounts apply to the ANNUAL INVOICE amount (matches backend checkout).
    const discount = getActiveDiscount(plan, discounts, billingPeriod);
    const annualBase = (plan.price || 0) * 12;
    const finalAnnual = discount
      ? (discount.discount_type === "percentage"
          ? Math.round(annualBase * (1 - (discount.value || 0) / 100))
          : Math.max(0, annualBase - (discount.value || 0)))
      : annualBase;
    const finalPrice = billingPeriod === "annual"
      ? Math.round(finalAnnual / 12)
      : (discount ? applyDiscount(plan.price, discount) : plan.price);
    const hasDiscount = !!discount && finalPrice < plan.price;
    return { discount, finalPrice, hasDiscount, annualBase, finalAnnual };
  };

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="pricing-page-root">
      <Seo
        title="Pricing | Fieldz AI Field Service Management Software"
        description="Simple, transparent pricing for field service businesses. Every plan includes scheduling, CRM, business phone & SMS, invoicing, and payments. Start with a free 7-day trial."
        path="/pricing"
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 md:pb-14 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--mk-blue-light)] border border-blue-200/60 mb-6">
            <Sparkles className="w-4 h-4 text-[var(--mk-blue)]" />
            <span className="text-sm font-semibold text-[var(--mk-blue)]">7-Day Free Trial • No Credit Card Required</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-9">
            Choose the plan that fits your business. Scale up or down anytime.
          </p>

          <div className="inline-flex items-center gap-1 rounded-full p-1 bg-slate-100 border border-slate-200" data-testid="pricing-billing-toggle">
            {["monthly", "annual"].map((mode) => (
              <button
                key={mode}
                onClick={() => setBillingPeriod(mode)}
                data-testid={`billing-${mode}-btn`}
                className={`px-5 py-2 rounded-full text-[13.5px] font-semibold transition-all ${
                  billingPeriod === mode ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                }`}
              >
                {mode === "monthly" ? "Monthly" : "Annual"}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Single-location plans */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loadingPlans ? (
            <div className="text-center text-slate-500 py-12">Loading plans...</div>
          ) : (
            <div className={`grid gap-6 ${singlePlans.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-3"}`}>
              {singlePlans.map((plan) => {
                const { discount, finalPrice, hasDiscount, finalAnnual } = renderPrice(plan);
                const isCurrentPlan =
                  user?.subscription_plan?.toLowerCase() === plan.plan_id?.toLowerCase() ||
                  user?.subscription_plan?.toLowerCase() === plan.name?.toLowerCase();
                const hasNoCheckoutTarget =
                  (typeof plan.price !== "number" || plan.price <= 0) ||
                  (!plan.stripe_price_id && !plan.stripe_price_id_annual);
                const contactMode = !!plan.is_contact_us || hasNoCheckoutTarget;
                const isPop = !!plan.is_popular;

                return (
                  <div
                    key={plan.plan_id}
                    className={`relative h-full flex flex-col rounded-2xl bg-white p-7 border ${
                      isPop
                        ? "border-[var(--mk-blue)] shadow-[0_20px_40px_-4px_rgba(0,70,190,0.14)] lg:scale-[1.03] z-10"
                        : "border-slate-200 shadow-[0_10px_30px_-3px_rgba(15,23,42,0.06)]"
                    }`}
                    data-testid={`pricing-card-${plan.plan_id}`}
                  >
                    {isPop && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] bg-[var(--mk-blue)] text-white shadow-md">
                        Most Popular
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="absolute -top-3.5 right-4 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-emerald-600 text-white">
                        Current Plan
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap mb-4">
                      <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                      {hasDiscount && discount.badge_label && !contactMode && (
                        <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700" data-testid={`discount-badge-${plan.plan_id}`}>
                          {discount.badge_label}
                        </span>
                      )}
                    </div>

                    <div className="mb-2">
                      {contactMode ? (
                        <span className="text-3xl font-extrabold text-slate-900">{plan.contact_us_text || "Contact us"}</span>
                      ) : (
                        <>
                          {hasDiscount && <span className="text-xl font-bold mr-2 line-through text-slate-300">${plan.price}</span>}
                          <span className="text-5xl font-extrabold tracking-tight text-slate-900">${finalPrice}</span>
                          <span className="text-[14px] text-slate-500 ml-1">/mo</span>
                        </>
                      )}
                    </div>
                    <div className="mb-5 min-h-[20px]">
                      {!contactMode && billingPeriod === "annual" && (
                        <p className="text-[13px] font-medium text-emerald-600">${finalAnnual}/yr billed annually</p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 mb-5 pb-5 border-b border-slate-100">
                      <Stat icon={Users} label={`${plan.max_users || 2} users`} />
                      <Stat icon={Phone} label={`${plan.dedicated_lines || 2} lines`} />
                      <Stat icon={MessageSquare} label={`${(plan.sms_limit || 1000).toLocaleString()} SMS`} />
                      <Stat icon={Clock} label={`${(plan.minutes_limit || 750).toLocaleString()} min`} />
                    </div>

                    <ul className="space-y-2.5 mb-7 flex-1">
                      {(plan.features || []).map((f, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[14px] text-slate-600">
                          <Check className="w-4 h-4 shrink-0 mt-0.5 text-[var(--mk-blue)]" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    {contactMode ? (
                      <button onClick={() => openDemoFor(plan)} className={`mk-btn w-full ${isPop ? "mk-btn-primary" : "mk-btn-outline"}`} data-testid={`single-plan-btn-${plan.plan_id}`}>
                        <Calendar className="w-4 h-4" /> Book a Demo
                      </button>
                    ) : (
                      <button
                        onClick={() => !isCurrentPlan && handleSelectPlan(plan)}
                        disabled={isCurrentPlan}
                        className={`mk-btn w-full ${isCurrentPlan ? "mk-btn-outline opacity-50 cursor-not-allowed" : isPop ? "mk-btn-primary" : "mk-btn-outline"}`}
                        data-testid={`single-plan-btn-${plan.plan_id}`}
                      >
                        {isCurrentPlan ? "Current Plan" : user ? "Upgrade" : "Start Free Trial"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Multi-location plans */}
      {!loadingPlans && mlPlans.length > 0 && (
        <section id="multi-location" className="py-16 md:py-20 bg-gradient-to-b from-emerald-50/70 to-white border-y border-emerald-100" data-testid="ml-pricing-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wide mb-4">
                <Building2 className="w-3.5 h-3.5" /> For Multi-Location Businesses
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">Running multiple locations?</h2>
              <p className="text-lg text-slate-600">
                Centralized dispatch, unified reporting, and single billing across all your branches.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-12">
              {[
                { title: "Centralized dispatch", body: "Route jobs to the right branch" },
                { title: "Unified reporting", body: "One view, every location" },
                { title: "Single billing", body: "One invoice for all branches" },
                { title: "Location-based permissions", body: "Branch managers see only their data" },
              ].map((b) => (
                <div key={b.title} className="rounded-xl bg-white border border-emerald-100 p-4 shadow-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-0.5">{b.title}</h3>
                      <p className="text-xs text-slate-500">{b.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={`grid gap-6 ${mlPlans.length === 3 ? "md:grid-cols-3" : mlPlans.length <= 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-2 lg:grid-cols-3"}`}>
              {mlPlans.map((plan) => {
                const { finalPrice, hasDiscount, finalAnnual } = renderPrice(plan);
                const hasNoCheckoutTarget =
                  (typeof plan.price !== "number" || plan.price <= 0) ||
                  (!plan.stripe_price_id && !plan.stripe_price_id_annual);
                const contactMode = !!plan.is_contact_us || hasNoCheckoutTarget;
                const isPop = !!plan.is_popular;
                return (
                  <div
                    key={plan.plan_id}
                    className={`relative h-full flex flex-col rounded-2xl bg-white p-7 border ${
                      isPop ? "border-emerald-500 shadow-[0_20px_40px_-4px_rgba(16,185,129,0.18)]" : "border-slate-200 shadow-sm"
                    }`}
                    data-testid={`ml-pricing-card-${plan.plan_id}`}
                  >
                    {isPop && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] bg-emerald-600 text-white shadow-md">
                        Most Popular
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                    </div>
                    <span className="inline-flex self-start px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] font-semibold mb-4">
                      Up to {plan.max_locations === -1 ? "Unlimited" : plan.max_locations} location{plan.max_locations === 1 ? "" : "s"}
                    </span>
                    <div className="mb-5">
                      {contactMode ? (
                        <span className="text-3xl font-extrabold text-emerald-700">{plan.contact_us_text || "Contact us"}</span>
                      ) : (
                        <>
                          {hasDiscount && <span className="text-xl font-bold mr-2 line-through text-slate-300">${plan.price}</span>}
                          <span className="text-5xl font-extrabold tracking-tight text-slate-900">${finalPrice}</span>
                          <span className="text-[14px] text-slate-500 ml-1">/mo</span>
                          {billingPeriod === "annual" && (
                            <p className="text-[13px] font-medium text-emerald-600 mt-1.5">${finalAnnual}/yr billed annually</p>
                          )}
                        </>
                      )}
                    </div>

                    <ul className="space-y-2.5 mb-7 flex-1">
                      {(plan.features || []).slice(0, 8).map((f, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[14px] text-slate-600">
                          <Check className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" /> {f}
                        </li>
                      ))}
                    </ul>

                    {contactMode ? (
                      <button onClick={() => openDemoFor(plan)} className={`mk-btn w-full ${isPop ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-[0_8px_20px_-6px_rgba(16,185,129,0.5)]" : "mk-btn-outline"}`} data-testid={`ml-pricing-btn-${plan.plan_id}`}>
                        <Calendar className="w-4 h-4" /> Book a Demo
                      </button>
                    ) : (
                      <button onClick={() => handleSelectPlan(plan)} className={`mk-btn w-full ${isPop ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-[0_8px_20px_-6px_rgba(16,185,129,0.5)]" : "mk-btn-outline"}`} data-testid={`ml-pricing-btn-${plan.plan_id}`}>
                        {user ? "Upgrade" : "Start Free Trial"} <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <p className="text-sm text-slate-500 mb-3">Not sure which plan fits? We'll help you pick.</p>
              <button className="mk-btn mk-btn-primary" onClick={() => openDemoFor(null)} data-testid="ml-book-demo-cta">
                <Calendar className="w-4 h-4" /> Book a demo
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Feature comparison table */}
      {!loadingPlans && singlePlans.length > 0 && (
        <section className="py-16 md:py-20 bg-[var(--mk-bg-2)]" data-testid="pricing-compare-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">Compare all features</h2>
              <p className="text-lg text-slate-600">See exactly what's included in each plan</p>
            </Reveal>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-4 px-5 text-[13px] font-semibold text-slate-500 uppercase tracking-wider">Features</th>
                    {singlePlans.map((plan) => {
                      const { finalPrice } = renderPrice(plan);
                      return (
                        <th key={plan.plan_id} className={`text-center py-4 px-4 ${plan.is_popular ? "bg-[var(--mk-blue-light)]/40" : ""}`}>
                          <span className={`block text-[16px] font-bold ${plan.is_popular ? "text-[var(--mk-blue)]" : "text-slate-900"}`}>{plan.name}</span>
                          <span className="text-slate-500 text-sm font-normal">${finalPrice}/mo</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {ALL_FEATURES.map((feature, idx) => (
                    <tr key={feature.key} className={`border-b border-slate-100 last:border-0 ${idx % 2 === 1 ? "bg-slate-50/60" : ""}`}>
                      <td className="py-4 px-5 text-[14.5px] text-slate-700 font-medium">{feature.label}</td>
                      {singlePlans.map((plan) => {
                        const value = getFeatureValue(plan, feature);
                        return (
                          <td key={plan.plan_id} className={`py-4 px-4 text-center ${plan.is_popular ? "bg-[var(--mk-blue-light)]/25" : ""}`}>
                            {feature.type === "boolean" ? (
                              value ? <Check className="w-5 h-5 text-emerald-600 mx-auto" /> : <X className="w-5 h-5 text-slate-300 mx-auto" />
                            ) : (
                              <span className="text-slate-900 font-semibold text-[14px]">{value}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-6">
              {singlePlans.map((plan) => (
                <div key={plan.plan_id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                    <div>
                      <h3 className={`text-lg font-bold ${plan.is_popular ? "text-[var(--mk-blue)]" : "text-slate-900"}`}>{plan.name}</h3>
                      <span className="text-slate-500 text-sm">${renderPrice(plan).finalPrice}/mo</span>
                    </div>
                    {plan.is_popular && (
                      <span className="px-2.5 py-1 rounded-full bg-[var(--mk-blue-light)] text-[var(--mk-blue)] text-xs font-bold">Popular</span>
                    )}
                  </div>
                  <ul className="space-y-3 mb-5">
                    {ALL_FEATURES.map((feature) => {
                      const value = getFeatureValue(plan, feature);
                      return (
                        <li key={feature.key} className="flex items-center justify-between">
                          <span className="text-slate-600 text-sm">{feature.label}</span>
                          {feature.type === "boolean" ? (
                            value ? <Check className="w-5 h-5 text-emerald-600" /> : <X className="w-5 h-5 text-slate-300" />
                          ) : (
                            <span className="text-slate-900 font-semibold text-sm">{value}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <button className="mk-btn mk-btn-primary w-full" onClick={() => handleSelectPlan(plan)}>
                    Get Started
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 text-center mb-12">Frequently asked questions</h2>
          <div className="space-y-4">
            {PRICING_FAQS.map((faq, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`pricing-faq-${idx}`}>
                <h3 className="text-slate-900 font-semibold mb-2 text-[15.5px]">{faq.q}</h3>
                <p className="text-slate-600 text-[14.5px] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-b from-blue-50/60 to-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-4">Ready to get started?</h2>
          <p className="text-lg text-slate-600 mb-8">Start your 7-day free trial today. No credit card required.</p>
          <Link to="/register" className="mk-btn mk-btn-primary mk-btn-lg" data-testid="pricing-final-cta">
            Start Free Trial <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <MarketingFooter />

      <BookDemoModal
        isOpen={demoOpen}
        onClose={() => { setDemoOpen(false); setDemoPlan(null); }}
        isDark={false}
        preselectedPlan={demoPlan}
      />
    </div>
  );
};

export default PricingPage;
